/* eslint-disable complexity */
import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import { formatCurrency } from '@futrli/localisation';

import * as styles from '../content.scss';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';

interface Props {
  unpaidCashInFromInvoices: string;
  unpaidCashOutFromBills: string;
  daysNumCustomersPaying: number;
  daysNumPayingToSuppliers: number;
}

export const CustomersSuppliersInfoList = ({
  unpaidCashInFromInvoices,
  unpaidCashOutFromBills,
  daysNumCustomersPaying,
  daysNumPayingToSuppliers
}: Props) => {
  const { currency } = useContext(PredictManagementContext);
  const intl = useIntl();
  const balance = Number(unpaidCashInFromInvoices) - Number(unpaidCashOutFromBills);

  return (
    <ul className={styles.bulletList}>
      {/* Balance */}
      <li>
        <FormattedMessage
          id="onboarding.you_have_value_cash_if_paid"
          defaultMessage="You’d have {currValue} {moreOrLess} cash if invoices and bills were paid"
          values={{
            currValue: (
              <span
                className={classNames(styles.boldText, {
                  [styles.positiveChange]: balance > 0,
                  [styles.negativeChange]: balance < 0
                })}
              >
                {`${balance > 0 ? '+' : ''}${formatCurrency(balance, intl, currency || '', 0, 0)}`}
              </span>
            ),
            moreOrLess:
              balance < 0
                ? intl.formatMessage({ id: 'common.less', defaultMessage: 'less' })
                : intl.formatMessage({ id: 'common.more', defaultMessage: 'more' })
          }}
        />
      </li>

      {/* Number of days customers paying */}
      <li>
        <FormattedMessage
          id="onboarding.customers_paying_you_in_value_days"
          defaultMessage="Customers are paying you in <b>{dayNum}</b> <b>{dayNum, plural, one {day} other {days}}</b>"
          values={{
            dayNum: daysNumCustomersPaying,
            b: chunks => <span className={styles.boldText}>{chunks}</span>
          }}
        />
      </li>

      {/* Number of days you are paying suppliers */}
      <li>
        <FormattedMessage
          id="onboarding.you_are_paying_suppliers_in_value_days"
          defaultMessage="You are paying suppliers in <b>{dayNum}</b> <b>{dayNum, plural, one {day} other {days}}</b>"
          values={{
            dayNum: daysNumPayingToSuppliers,
            b: chunks => <span className={styles.boldText}>{chunks}</span>
          }}
        />
      </li>

      {/* Due invoices total */}
      <li>
        <FormattedMessage
          id="onboarding.due_invoices_total_value"
          defaultMessage="Due invoices total {dueInvoicesTotal}"
          values={{
            dueInvoicesTotal: (
              <span className={styles.boldText}>
                {formatCurrency(Number(unpaidCashInFromInvoices), intl, currency || '', 0, 0)}
              </span>
            )
          }}
        />
      </li>

      {/* Due bills total */}
      <li>
        <FormattedMessage
          id="onboarding.due_bills_total_value"
          defaultMessage="Due bills total {dueBillsTotal}"
          values={{
            dueBillsTotal: (
              <span className={styles.boldText}>
                {formatCurrency(Number(unpaidCashOutFromBills), intl, currency || '', 0, 0)}
              </span>
            )
          }}
        />
      </li>
    </ul>
  );
};
